import React, { useState, useEffect } from "react";
import { MapPinIcon } from "@heroicons/react/20/solid";
import SearchLookup from "./SearchLookup/SearchLookup";
import FixedLocationSelector from "./FixedLocationSelector";
import { ADDRESS_TYPES } from "../../config/availability";
import {
  searchAddresses,
  searchRegions,
  searchPublicAddresses,
} from "../../api/mapApi";
import { getThemedBg } from "../../utility/themeVars";

const AddressLookupNative = ({
  label,
  onSelect,
  isError,
  placeholder = "Search for an address...",
  addressType = ADDRESS_TYPES.ADDRESS,
  maxDisplayLength = 200,
  disabled = false,
  required = false,
  optional = false,
  defaultValue = null,
  defaultOptions = [],
  isPublic = false,
  bookingPageSlug = null,
  fixedLocationMode = false,
  showSearchOption = true,
  themedBg = null,
  searchBias = null,
  themeMode = null,
  primaryColor = null,
}) => {
  // Build themedBg from themeMode/primaryColor when provided (public booking pages)
  const resolvedThemedBg = themeMode ? getThemedBg(themeMode, primaryColor) : themedBg;

  const [showSearch, setShowSearch] = useState(
    !fixedLocationMode || defaultOptions.length === 0
  );
  const [selectedFixedLocation, setSelectedFixedLocation] = useState(
    defaultValue &&
      defaultOptions.find((opt) => opt.placeId === defaultValue.placeId)
      ? defaultOptions.find((opt) => opt.placeId === defaultValue.placeId)
      : null
  );

  // Update showSearch when defaultOptions loads
  useEffect(() => {
    if (fixedLocationMode && defaultOptions.length > 0) {
      setShowSearch(false);
    }
  }, [fixedLocationMode, defaultOptions.length]);

  // Update selectedFixedLocation when defaultOptions loads
  useEffect(() => {
    if (defaultValue && defaultOptions.length > 0) {
      const matchingLocation = defaultOptions.find(
        (opt) => opt.placeId === defaultValue.placeId
      );
      if (matchingLocation && !selectedFixedLocation) {
        setSelectedFixedLocation(matchingLocation);
      }
    }
  }, [defaultValue, defaultOptions, selectedFixedLocation]);

  const searchFunction = async (query) => {
    try {
      // If no query and we have defaultOptions, return them
      if (!query && defaultOptions.length > 0) {
        return defaultOptions;
      }

      let results;
      if (isPublic) {
        // Use public endpoints for public booking pages (only ADDRESS type supported)
        results = await searchPublicAddresses(query, bookingPageSlug, searchBias);
      } else {
        // Use authenticated endpoints for logged-in users
        if (addressType === ADDRESS_TYPES.REGION) {
          results = await searchRegions(query);
        } else {
          results = await searchAddresses(query);
        }
      }

      // If we have both API results and defaultOptions, combine them
      if (defaultOptions.length > 0) {
        return [...defaultOptions, ...results];
      }

      return results;
    } catch (error) {
      console.error("Address search error:", error);
      // If API fails but we have defaultOptions, return them
      if (defaultOptions.length > 0) {
        return defaultOptions;
      }
      throw error;
    }
  };

  const handleFixedLocationSelect = (location) => {
    setSelectedFixedLocation(location);
    onSelect?.(location);
  };

  const handleSearchClick = () => {
    setShowSearch(true);
  };

  const handleSearchSelect = (address) => {
    onSelect?.(address);
  };

  // If in fixed location mode and have options, show the selector
  if (fixedLocationMode && !showSearch && defaultOptions.length > 0) {
    return (
      <FixedLocationSelector
        locations={defaultOptions}
        selectedLocation={selectedFixedLocation}
        onSelect={handleFixedLocationSelect}
        label={label}
        showSearchOption={showSearchOption}
        onSearchClick={handleSearchClick}
      />
    );
  }

  return (
    <SearchLookup
      label={label}
      optional={optional}
      placeholder={placeholder}
      icon={<MapPinIcon className="size-5 text-ink-400" aria-hidden={true} />}
      searchFunction={searchFunction}
      onSelect={handleSearchSelect}
      isError={isError}
      maxDisplayLength={maxDisplayLength}
      disabled={disabled}
      required={required}
      defaultValue={defaultValue}
      clearOnSelect={addressType === ADDRESS_TYPES.REGION}
      themedBg={resolvedThemedBg}
    />
  );
};

export default AddressLookupNative;
